import { z } from 'zod';
import type { HelpdeskQuery } from '../api/queries';

export const helpdeskViews = ['queue', 'in-progress', 'closed', 'no-reply'] as const;
export const helpdeskStatuses = [
  'new',
  'rejected',
  'under_consideration',
  'planned',
  'in_progress',
  'complete',
  'to_deploy',
  'done',
  'canceled',
] as const;
export const helpdeskSearchSchema = z.object({
  helpdeskView: z.enum(helpdeskViews).optional(),
  helpdeskPage: z.coerce.number().int().min(1).max(100000).optional(),
  helpdeskQ: z.string().trim().max(200).optional(),
  helpdeskTaskId: z.string().trim().max(200).optional(),
  helpdeskStatus: z.enum(helpdeskStatuses).optional(),
  helpdeskAssigneeId: z
    .uuid()
    .transform((id) => id.toLowerCase())
    .optional(),
  helpdeskOriginIds: z
    .preprocess((value) => (typeof value === 'string' ? [value] : value), z.array(z.uuid()).max(50))
    .optional()
    .transform((values) =>
      values?.length ? [...new Set(values.map((id) => id.toLowerCase()))].sort() : undefined
    ),
});
export type HelpdeskSearch = z.infer<typeof helpdeskSearchSchema>;

export function toHelpdeskQuery(search: HelpdeskSearch, customerId?: string): HelpdeskQuery {
  const query = search.helpdeskQ?.trim();
  const searchedTaskId =
    query &&
    /^F-(?:\d+(?:-\d+)?|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/u.test(query)
      ? query
      : undefined;
  return {
    view: search.helpdeskView ?? 'queue',
    page: search.helpdeskPage ?? 1,
    q: searchedTaskId ? undefined : query || undefined,
    taskId: searchedTaskId ?? (search.helpdeskTaskId || undefined),
    status: search.helpdeskStatus,
    assigneeId: search.helpdeskAssigneeId,
    originIds: search.helpdeskOriginIds,
    ...(customerId === undefined ? {} : { customerId }),
  };
}

/** A filter change always returns to the first page; the selected view survives reset. */
export function changeHelpdeskFilters(
  search: HelpdeskSearch,
  next: Partial<Omit<HelpdeskSearch, 'helpdeskPage'>>
): HelpdeskSearch {
  return { ...search, ...next, helpdeskPage: undefined };
}
